/* ══════════════════════════════════════════════════════════════════
   Der gemeinsame Zeitraum und der eigene eines Postens — rein, ohne
   React, damit die Tests ihn direkt laden. Verwendet vom Abschluss
   „Jetzt vermieten" (`bausteine/MerklisteAbschluss.tsx`) und von der
   Merkliste rechts (`bausteine/Merkliste.tsx`).

   Luca, 08.10.2026: *„Wenn dort für alle Posten 9. bis 15. steht, kann ich
   das auf Positionsebene nicht verändern, wenn ich ein anderes Datum wählen
   will."* Soll: Ein Posten darf einen eigenen Zeitraum haben, der vom
   gemeinsamen abweicht. Ändert man den gemeinsamen, folgen nur die Posten
   ohne eigenen.

   **Der Posten trägt die Entscheidung, nicht die Abweichung.** Würde
   „eigen" allein an der Abweichung abgelesen, wäre der gemeinsame Zeitraum
   bei zwei Posten, von denen einer geändert ist, ein Münzwurf — welcher
   von beiden ist der gemeinsame? Deshalb merkt sich der Posten
   `eigenerZeitraum`, sobald ihn jemand einzeln auf einen anderen Zeitraum
   setzt, und verliert es mit „wie alle".
   ══════════════════════════════════════════════════════════════════ */

/** Was diese Rechnung von einem Posten braucht. */
export type Zeitposten = {
  id: string;
  art?: string;
  von: string;
  bis: string;
  eigenerZeitraum?: boolean;
};

export type Zeitspanne = { von: string | null; bis: string | null };

const istMiete = (p: { art?: string }): boolean => (p.art ?? "miete") === "miete";

/**
 * **Der Zeitraum, der für alle gilt.** Der der ersten Miete ohne eigenen
 * Zeitraum; gibt es keine (alle eigen, oder gar keine Miete), der aus der
 * Feldzeile von „Vermieten" — dieselbe Quelle, kein zweiter Stand.
 */
export function gemeinsamerMietzeitraum(posten: readonly Zeitposten[], vermieten: Zeitspanne): Zeitspanne {
  const anker = posten.find((p) => istMiete(p) && !p.eigenerZeitraum && p.von && p.bis);
  return anker ? { von: anker.von, bis: anker.bis } : { von: vermieten.von, bis: vermieten.bis };
}

/** Weicht der Posten vom gemeinsamen Zeitraum ab? Dann trägt er die Marke „eigener Zeitraum". */
export function weichtAb(p: Zeitposten, gemeinsam: Zeitspanne): boolean {
  if (!gemeinsam.von || !gemeinsam.bis) return false;
  return p.von !== gemeinsam.von || p.bis !== gemeinsam.bis;
}

/**
 * **Ein Posten bekommt einzeln einen Zeitraum.** Weicht er damit vom
 * gemeinsamen ab, ist es seiner; trifft er ihn wieder, folgt er wieder
 * allen (dasselbe wie „wie alle").
 */
export function zeitraumEinzeln(
  neu: { von: string; bis: string },
  gemeinsam: Zeitspanne,
): { von: string; bis: string; eigenerZeitraum: true | undefined } {
  const gleich = neu.von === gemeinsam.von && neu.bis === gemeinsam.bis;
  return { von: neu.von, bis: neu.bis, eigenerZeitraum: gleich ? undefined : true };
}

/** „Wie alle": zurück auf den gemeinsamen Zeitraum, die Marke fällt weg. */
export function zeitraumWieAlle(gemeinsam: Zeitspanne): { von: string; bis: string; eigenerZeitraum: undefined } | null {
  if (!gemeinsam.von || !gemeinsam.bis) return null;
  return { von: gemeinsam.von, bis: gemeinsam.bis, eigenerZeitraum: undefined };
}

/**
 * **Der gemeinsame Zeitraum ändert sich — wer folgt?** Jede Miete ohne
 * eigenen Zeitraum, und eine Leistung, die den alten gemeinsamen Zeitraum
 * als Termin trug (P10-02); eine Leistung mit eigenem Tag bleibt, wo sie
 * ist. Zurück kommen die Änderungen je Posten.
 */
export function folgenDemGemeinsamen(
  posten: readonly Zeitposten[],
  alt: Zeitspanne,
  neu: { von: string; bis: string },
): { id: string; von: string; bis: string }[] {
  return posten
    .filter((p) => (istMiete(p)
      ? !p.eigenerZeitraum
      : p.art === "leistung" && Boolean(p.von) && p.von === alt.von && p.bis === alt.bis))
    .filter((p) => p.von !== neu.von || p.bis !== neu.bis)
    .map((p) => ({ id: p.id, von: neu.von, bis: neu.bis }));
}
